// @ts-check
import React, { useCallback } from 'react';
import { Formik } from 'formik';
import { useMutation } from 'react-query';
import { usePortalConfig } from 'shared/context/PortalConfig';
import api from 'utils/api';
import { Button } from 'preferral-ui/components/Button';
import { FilterModel } from '../FilterPanel'; // eslint-disable-line no-unused-vars
import {
  localDateRegex,
  localDateToISO,
  mmDdYyyy,
} from 'preferral-common/utils/dateFormatters';
import { DatePickerField } from 'preferral-ui/components/formik/DatePickerField';
import { TextField } from 'preferral-ui/components/formik/TextField';
import { decamelizeKeys } from 'humps';
import { FormStatusErrors } from 'preferral-ui/components/formik/FormStatusErrors';
import * as Yup from 'yup';

/** @typedef {{
  windowStart: string;
  windowFinish: string;
  password: string;
}} FormValues */

/** @type { Yup.SchemaOf<FormValues> } */
const validationSchema = Yup.object()
  .shape({
    windowStart: Yup.string()
      .matches(localDateRegex, 'Invalid date')
      .required('Required'),
    windowFinish: Yup.string()
      .matches(localDateRegex, 'Invalid date')
      .required('Required'),
    password: Yup.string()
      .min(5, 'Must be at least 5 characters')
      .required('Required'),
  })
  .required();

/** @typedef {{
  filter: FilterModel;
  onSuccess(): void;
}} NewDataExportFormProps */

/** @type { React.FC<NewDataExportFormProps> } */
export const NewDataExportForm = (props) => {
  const { filter, onSuccess } = props;

  const { apiPrefix } = usePortalConfig();

  const mutation = useMutation((/** @type { FormValues } */ exportInput) =>
    api
      .post(`${apiPrefix}/data_exports`, {
        data_export: decamelizeKeys(exportInput),
      })
      .then((resp) => resp.data)
  );

  const onSubmit = useCallback(
    (values, formikHelpers) => {
      const { setStatus, setSubmitting } = formikHelpers;

      setStatus({ errors: null });

      const input = {
        ...values,
        windowStart: localDateToISO(values.windowStart),
        windowFinish: localDateToISO(values.windowFinish),
      };

      return mutation.mutateAsync(input).then(
        (data) => {
          onSuccess();
        },
        (err) => {
          setSubmitting(false);
          if (err && err.errors && err.errors._error) {
            setStatus({
              errors: err.errors._error.map((e) => ({ key: '', message: e })),
            });
          } else {
            return Promise.reject(err);
          }
        }
      );
    },
    [onSuccess, mutation]
  );

  return (
    <div className="NewDataExportForm">
      <p className="py-2">
        After submitting this form to create a data export, the encrypted file
        will be emailed to you.
      </p>
      <p className="pt-2 pb-5">
        NOTE: It may take up to an hour to receive your exported data.
      </p>
      <Formik
        initialValues={{
          windowStart: mmDdYyyy(filter.dateRange.start),
          windowFinish: mmDdYyyy(filter.dateRange.finish),
          password: '',
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, isSubmitting, status }) => (
          <form onSubmit={handleSubmit}>
            <FormStatusErrors status={status} />
            <div className="grid grid-cols-2 gap-4">
              <DatePickerField
                name="windowStart"
                label="Start Date"
                icon={['far', 'calendar-alt']}
              />

              <DatePickerField
                name="windowFinish"
                label="End Date"
                icon={['far', 'calendar-alt']}
              />
            </div>

            <div className="mt-3">
              <TextField
                type="password"
                icon="lock"
                name="password"
                label="Encryption Password"
                placeholder="••••••••••"
              />
            </div>

            <div className="mt-6 text-center">
              <Button
                type="submit"
                kind="primary"
                color="blue"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                Submit
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};
